/**
 * Mobile period chips (AD-020): they only map to presets that already exist
 * in the pages' lists and drive the same setters as `DateRangeFilter`, in the
 * same order, so URL state (`useMetricsDateFilter`) and local state keep
 * working unchanged.
 */
import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import { getLuxonLocale } from '@/lib/utils';

export const PERIOD_CHIP_PRESETS = [
  'today',
  'last_7_days',
  'last_30_days',
  'last_3_months',
] as const;

export type PeriodChipPreset = (typeof PERIOD_CHIP_PRESETS)[number];

export interface PresetRange {
  label: string;
  value: string;
  from: string;
  to: string;
}

export interface PeriodSetters {
  setPredefinedDateRange: (v: string | undefined) => void;
  setFromDate: (iso: string) => void;
  setToDate: (iso: string) => void;
}

function isChipPreset(value: string | undefined): value is PeriodChipPreset {
  return PERIOD_CHIP_PRESETS.some((p) => p === value);
}

/** Chips whose preset the page offers, in chip order. */
export function getVisibleChips(ranges: PresetRange[]): PeriodChipPreset[] {
  return PERIOD_CHIP_PRESETS.filter((p) => ranges.some((r) => r.value === p));
}

export function getSelectedChip(
  predefinedDateRange: string | undefined
): PeriodChipPreset | 'custom' {
  return isChipPreset(predefinedDateRange) ? predefinedDateRange : 'custom';
}

/**
 * Label of the "Personalizado" chip: the preset's own label when the period
 * is a preset that is not a chip (e.g. "Desde o início"), else the custom
 * dates ("3–9 out", "28 set – 4 out", with the year when it is not the
 * current one), else `null`.
 */
export function getCustomChipLabel(input: {
  predefinedDateRange: string | undefined;
  ranges: PresetRange[];
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
  locale: Locales;
}): string | null {
  const { predefinedDateRange, ranges, fromDate, toDate, locale } = input;

  if (predefinedDateRange !== undefined) {
    if (isChipPreset(predefinedDateRange)) return null;
    const preset = ranges.find((r) => r.value === predefinedDateRange);
    if (preset) return preset.label;
  }

  if (!fromDate || !toDate) return null;
  const luxonLocale = getLuxonLocale(locale);
  const from = DateTime.fromISO(fromDate).setLocale(luxonLocale);
  const to = DateTime.fromISO(toDate).setLocale(luxonLocale);
  if (!from.isValid || !to.isValid) return null;

  return formatRange(from, to, locale);
}

function formatRange(from: DateTime, to: DateTime, locale: Locales): string {
  const month = (d: DateTime) => d.toFormat('MMM').replace(/\.$/, '');
  const currentYear = DateTime.now().year;
  const sameYear = from.year === to.year;
  const showYear = from.year !== currentYear || to.year !== currentYear;
  const sameMonth = sameYear && from.month === to.month;
  const sameDay = sameMonth && from.day === to.day;

  if (locale === 'en') {
    const day = (d: DateTime, withYear: boolean) =>
      `${month(d)} ${d.day}${withYear ? `, ${d.year}` : ''}`;
    const yearSuffix = showYear ? `, ${to.year}` : '';
    if (sameDay) return `${day(to, false)}${yearSuffix}`;
    if (sameMonth) return `${month(from)} ${from.day}–${to.day}${yearSuffix}`;
    if (sameYear) return `${day(from, false)} – ${day(to, false)}${yearSuffix}`;
    return `${day(from, true)} – ${day(to, true)}`;
  }

  const day = (d: DateTime, withYear: boolean) =>
    `${d.day} ${month(d)}${withYear ? ` ${d.year}` : ''}`;
  const yearSuffix = showYear ? ` ${to.year}` : '';
  if (sameDay) return `${day(to, false)}${yearSuffix}`;
  if (sameMonth) return `${from.day}–${to.day} ${month(to)}${yearSuffix}`;
  if (sameYear) return `${day(from, false)} – ${day(to, false)}${yearSuffix}`;
  return `${day(from, true)} – ${day(to, true)}`;
}

/** Same sequence as `DateRangeFilter`: preset → from → to. */
export function applyPreset(
  setters: PeriodSetters,
  ranges: PresetRange[],
  value: PeriodChipPreset
): void {
  const preset = ranges.find((r) => r.value === value);
  if (!preset) return;
  setters.setPredefinedDateRange(value);
  setters.setFromDate(preset.from);
  setters.setToDate(preset.to);
}

/**
 * Same sequence as a custom pick in `DateRangeFilter`: clear the preset, then
 * `from` at the start of its day, then `to` at the end of its day, never
 * later than now.
 */
export function applyCustomRange(
  setters: PeriodSetters,
  from: Date,
  to: Date
): void {
  const now = DateTime.now();
  const end = DateTime.fromJSDate(to).endOf('day');
  const fromIso = DateTime.fromJSDate(from).startOf('day').toISO();
  const toIso = (end > now ? now : end).toISO();
  if (!fromIso || !toIso) return;

  setters.setPredefinedDateRange(undefined);
  setters.setFromDate(fromIso);
  setters.setToDate(toIso);
}
